import React from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { BarChart3, PieChart } from 'lucide-react';

const STATUS_FALLBACK = '#94a3b8';
const CARD = 'rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-18px_rgba(16,24,40,0.16)]';
const TITLE = 'flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500';

/** Status rows with dots — stays readable when one status holds 95%. */
export const QuotationStatusBar = ({ data = [] }) => {
  const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
  return (
    <section className={CARD} data-testid="quotation-status-bar">
      <div className="flex items-center justify-between gap-3">
        <h2 className={TITLE}>
          <PieChart className="h-4 w-4 text-blue-600" strokeWidth={1.75} /> Quotation status
        </h2>
        <span className="font-mono text-xs tabular-nums text-slate-400">{total} in period</span>
      </div>
      {!total ? (
        <p className="mt-6 text-sm text-slate-500">No quotations in this period.</p>
      ) : (
        <>
          <div className="mt-4 flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
            {data.map((item) => (
              <div
                key={item.name}
                title={`${item.name}: ${item.value}`}
                style={{ width: `${(item.value / total) * 100}%`, background: item.color || STATUS_FALLBACK }}
                data-testid={`status-segment-${item.name}`}
              />
            ))}
          </div>
          <ul className="mt-4 divide-y divide-slate-100">
            {data.map((item) => (
              <li key={item.name} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: item.color || STATUS_FALLBACK }} />
                <span className="flex-1 text-slate-600">{item.name}</span>
                <span className="font-mono font-semibold tabular-nums text-slate-900">{item.value}</span>
                <span className="w-12 text-right font-mono text-xs tabular-nums text-slate-400">
                  {Math.round((item.value / total) * 100)}%
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};

/** Step area chart — one or two months of data still looks intentional. */
export const QuotationTrendChart = ({ data = [] }) => {
  const hasData = data.some((point) => point.received > 0 || point.quoted > 0);
  return (
    <section className={CARD} data-testid="quotation-trend-chart">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className={TITLE}>
          <BarChart3 className="h-4 w-4 text-blue-600" strokeWidth={1.75} /> Quotation activity
        </h2>
        <span className="text-xs text-slate-400">Created vs approved · last 6 months</span>
      </div>
      <div className="mt-4 h-[240px] min-h-[240px] w-full">
        {hasData ? (
          <ResponsiveContainer width="100%" height={240} minWidth={0}>
            <AreaChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="createdFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="approvedFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 12, fontSize: 12, boxShadow: '0 8px 24px -16px rgba(16,24,40,0.3)' }} />
              <Area type="step" dataKey="received" name="Created" stroke="#2563EB" fill="url(#createdFill)" strokeWidth={2} />
              <Area type="step" dataKey="quoted" name="Approved / sent" stroke="#10B981" fill="url(#approvedFill)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">
            No quotation activity in this period
          </div>
        )}
      </div>
      <div className="flex gap-5 text-xs text-slate-500">
        <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-[#2563EB]" /> Created</span>
        <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full bg-[#10B981]" /> Approved / sent</span>
      </div>
    </section>
  );
};
